/**
 * A row of pressable chips that choose a scope — "All sources 6,900 · Upload
 * 2,400" — the concept's `.sb-scope-chip[aria-pressed]`.
 *
 * ## WHY TOGGLE BUTTONS, AND NOT RADIOS OR TABS
 *
 * A chip row filters what the rest of the screen shows; it does not switch to
 * another panel, so it is not Tabs. Radios would be right for `single`, but a
 * radio group's keyboard model (arrows move AND select) means arrowing past a
 * chip refilters a long list on every keypress, and the same row has to work
 * as `multiple`, where radios cannot. So both modes are a `role="group"` of
 * `<button aria-pressed>`, reached with Tab and pressed with Space or Enter —
 * the model the Credits page's filter chips already use, so a keyboard user
 * meets one behaviour across the product. No roving tabindex, deliberately,
 * for the same reason.
 *
 * In `single` mode exactly one chip is pressed and pressing it again keeps it
 * pressed: a scope with nothing chosen is not a state the screens behind this
 * can draw. In `multiple` mode a chip toggles.
 *
 * ## THE COLOURS
 *
 * Pressed is `bg-brand-a/24` with white text — **not** `/20`. The app shell's
 * active nav item is the one `bg-brand-a/20` on every page and
 * `verify-nav-shell` counts it as exactly one; the tint ladder for new code
 * skips 20 so nothing else can be counted as a second current page. The count
 * is sans `tabular-nums` (mono's dotted zero reads as an 8 at this size) in
 * muted, and in `brand-ink` when pressed — muted on a /24 brand fill would be
 * the 4.5:1 edge. A chip's optional dot is an identity tone with the label
 * beside it, never a status colour.
 *
 * Pressed is also carried by something that is not colour. Under forced
 * colours (Windows High Contrast) the fill, the text colour and the border hue
 * are all replaced by system colours, and the pressed chip would vanish into
 * its row; `Tabs` already answers this with an underline, and a pressed chip
 * here takes the same `forced-colors:underline`, plus `font-medium` in every
 * mode so the state is not a 1.4:1 fill change alone (slice 66 review).
 *
 * ## ITS NAME AND ITS DESCRIPTION
 *
 * `aria-describedby` and `aria-invalid` are forwarded to the `role="group"`,
 * not left on the outer wrapper with the rest of the props: a description or
 * an error is about the choice, and a wrapper with no role is not something a
 * screen reader announces. A single chip can point at its own description
 * with `describedBy` — the admin feature picker's one-line explanation of each
 * part is a separate element, and without the link it was no longer part of
 * what the control says.
 *
 * ## ZERO IS NOT "NOT MEASURED"
 *
 * `count: null` draws `NoFigure`; `count: undefined` draws no count at all.
 * A chip whose count is not known never shows `0`.
 */
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import type { IdentTone } from '../lib/identity';
import { DOT_CLASS } from '../lib/tone-classes';
import { NoFigure } from './FigureState';

export interface ScopeChipItem {
  readonly value: string;
  readonly label: ReactNode;
  /** `null` is "not measured"; omit for a chip with no count. */
  readonly count?: number | null;
  /** A source's identity dot, drawn before the label. */
  readonly tone?: IdentTone;
  /** The id of an element that describes this one chip. */
  readonly describedBy?: string;
}

interface ScopeChipsBase extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  /** The group's accessible name — "Build from". */
  label: string;
  items: readonly ScopeChipItem[];
  /** Shown before the chips — usually a Kicker. Not part of the group. */
  leading?: ReactNode;
}

export type ScopeChipsProps = ScopeChipsBase &
  (
    | { selection: 'single'; value: string; onValueChange: (value: string) => void }
    | { selection: 'multiple'; value: readonly string[]; onValueChange: (value: readonly string[]) => void }
  );

const EN_IN = new Intl.NumberFormat('en-IN');

export function ScopeChips(props: ScopeChipsProps) {
  // `selection` and `onValueChange` are read through `props` below, so the
  // union narrows; they are named here only to keep them out of `rest`.
  const {
    label,
    items,
    leading,
    className,
    selection: _selection,
    value,
    onValueChange: _onValueChange,
    'aria-describedby': describedBy,
    'aria-invalid': invalid,
    ...rest
  } = props;

  const isPressed = (itemValue: string): boolean =>
    typeof value === 'string' ? value === itemValue : value.includes(itemValue);

  const press = (itemValue: string): void => {
    if (props.selection === 'single') {
      props.onValueChange(itemValue);
    } else {
      const current = props.value;
      props.onValueChange(
        current.includes(itemValue) ? current.filter((entry) => entry !== itemValue) : [...current, itemValue],
      );
    }
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-x-3.5 gap-y-2.5', className)} {...rest}>
      {leading}
      <div
        role="group"
        aria-label={label}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        className="flex flex-wrap gap-1.5"
      >
        {items.map((item) => {
          const pressed = isPressed(item.value);
          return (
            <button
              key={item.value}
              type="button"
              aria-pressed={pressed}
              aria-describedby={item.describedBy}
              onClick={() => press(item.value)}
              className={cn(
                'inline-flex cursor-pointer items-center gap-[7px] rounded-full border px-[11px] py-[5px] text-[0.84rem] leading-snug',
                'transition-colors duration-150',
                pressed
                  ? 'border-brand-b/60 bg-brand-a/24 font-medium text-white forced-colors:underline'
                  : 'border-line-strong bg-white/[0.04] text-muted hover:bg-white/[0.08] hover:text-text',
                focusRing,
              )}
            >
              {item.tone !== undefined && (
                <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full', DOT_CLASS[item.tone])} />
              )}
              <span>{item.label}</span>
              {item.count !== undefined && (
                <span className={cn('text-[0.72rem] tabular-nums', pressed ? 'text-brand-ink' : 'text-muted')}>
                  {item.count === null ? <NoFigure state="not_measured" /> : EN_IN.format(item.count)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
